import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { 
  Package, Plus, Search, AlertTriangle, X, Loader2,
  Minus, ChevronUp, Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';

const typeBadges = {
  supplement: 'badge-success',
  wellness: 'badge-info',
  package: 'badge-purple',
  consultation: 'badge-info',
  program: 'badge-purple',
  assessment: 'badge-warning',
  follow_up: 'bg-teal-50 text-teal-700 border-teal-200'
};

const Inventory = () => {
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(null);
  const [showAddService, setShowAddService] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stockQuantity, setStockQuantity] = useState(1);
  const [stockType, setStockType] = useState('add');
  const [stockMode, setStockMode] = useState('individual'); // 'individual' or 'package'
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name, type: 'product' | 'service' }

  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: 'supplement',
    selling_price: '',
    purchase_price: '',
    quantity_in_stock: '',
    min_stock_level: '',
    unit_type: 'tablets',
    total_units_per_package: '',
    allow_partial_sale: false,
    expiry_date: '',
    description: '',
    packages_quantity: '',
    units_per_package: ''
  });

  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    price: '',
    duration_minutes: '',
    service_type: 'consultation',
    is_active: true
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [productsRes, servicesRes] = await Promise.all([
        api.get('/inventory/products'),
        api.get('/pos/services')
      ]);
      setProducts(productsRes.data || []);
      setServices(servicesRes.data || []);
    } catch (error) {
      console.error('Failed to load inventory:', error);
      toast.error('Failed to load inventory');
      setProducts([]);
      setServices([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Build payload - server will calculate quantity_in_stock from packages_quantity * units_per_package
      const payload = {
        ...formData,
        // Send packages_quantity and units_per_package if partial sale is enabled
        packages_quantity: formData.allow_partial_sale ? formData.packages_quantity : 0,
        units_per_package: formData.allow_partial_sale ? formData.units_per_package : null
      };
      // Don't send quantity_in_stock if partial sale with packages is set- server calculates it
      if (formData.allow_partial_sale && formData.packages_quantity && formData.units_per_package) {
        delete payload.quantity_in_stock;
      }
      await api.post('/inventory/products', payload);
      toast.success('Product added successfully');
      setShowAddModal(false);
      setFormData({
        name: '', sku: '', category: 'supplement', selling_price: '',
        purchase_price: '', quantity_in_stock: '', min_stock_level: '',
        unit_type: 'tablets', total_units_per_package: '',
        allow_partial_sale: false, expiry_date: '', description: '',
        packages_quantity: '', units_per_package: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add product');
    }
    setSubmitting(false);
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/inventory/services', serviceForm);
      toast.success('Service added successfully');
      setShowAddService(false);
      setServiceForm({
        name: '', description: '', price: '',
        duration_minutes: '', service_type: 'consultation', is_active: true
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add service');
    }
    setSubmitting(false);
  };

  const handleStockUpdate = async () => {
    if (stockQuantity <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/inventory/products/${showStockModal.id}/adjust`, {
        quantity: stockQuantity,
        adjustment_type: stockType,
        stock_type: stockMode // 'individual' or 'package'
      });
      toast.success(`Stock ${stockType === 'add' ? 'added' : 'removed'} successfully`);
      setShowStockModal(null);
      setStockQuantity(1);
      setStockMode('individual');
      fetchData();
    } catch (error) {
      console.error('Failed to update stock:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to update stock');
    }
    setSubmitting(false);
  };

  const toggleServiceStatus = async (service) => {
    try {
      await api.put(`/inventory/services/${service.id}`, {
        is_active: !service.is_active
      });
      toast.success(`Service ${service.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setSubmitting(true);
    try {
      if (deleteConfirm.type === 'product') {
        await api.delete(`/inventory/products/${deleteConfirm.id}`);
        toast.success('Product deleted successfully');
      } else {
        await api.delete(`/inventory/services/${deleteConfirm.id}`);
        toast.success('Service deleted successfully');
      }
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(`Failed to delete ${deleteConfirm.type}`);
    }
    setSubmitting(false);
  };

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredServices = services.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-brand-blue-100 rounded-lg">
            <Package size={24} className="text-brand-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-dark">Inventory</h1>
            <p className="text-sm text-neutral-gray">Manage products and services</p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowAddService(true)}
            className="px-4 py-2.5 border border-brand-blue text-brand-blue rounded-xl text-sm font-medium hover:bg-brand-blue-50 transition-colors"
          >
            <span className="flex items-center space-x-2">
              <Plus size={16} />
              <span>Add Service</span>
            </span>
          </button>
          {activeTab === 'products' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Product</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs & Search */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex space-x-1 bg-neutral-light rounded-xl p-1">
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'products' ? 'bg-white text-neutral-dark shadow-sm' : 'text-neutral-gray hover:text-neutral-dark'
            }`}
          >
            Products
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'services' ? 'bg-white text-neutral-dark shadow-sm' : 'text-neutral-gray hover:text-neutral-dark'
            }`}
          >
            Services
          </button>
        </div>

        <div className="relative w-full md:w-64">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-gray" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-neutral-medium rounded-xl text-sm focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
          />
        </div>
      </div>

      {/* Content */}
      {activeTab === 'products' ? (
        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-light">
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Product</th>
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">SKU</th>
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Category</th>
                  <th className="text-right text-sm font-medium text-neutral-gray px-4 py-3">Stock</th>
                  <th className="text-right text-sm font-medium text-neutral-gray px-4 py-3">Price</th>
                  <th className="text-right text-sm font-medium text-neutral-gray px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-neutral-gray">No products found</td>
                  </tr>
                ) : filteredProducts.map(product => (
                  <tr key={product.id} className="border-t border-neutral-medium hover:bg-neutral-light transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-neutral-dark">{product.name}</p>
                        {product.description && (
                          <p className="text-xs text-neutral-gray mt-0.5">{product.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-gray">{product.sku}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeBadges[product.category] || 'bg-gray-100 text-gray-700'}`}>
                        {product.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div>
                        {product.allow_partial_sale && product.packages_quantity > 0 && product.units_per_package > 0 ? (
                          <>
                            <span className={`text-sm font-medium ${
                              product.quantity_in_stock <= product.min_stock_level
                                ? 'text-brand-red'
                                : 'text-brand-green'
                            }`}>
                              {product.packages_quantity} bottles/packs
                            </span>
                            <span className="text-xs text-neutral-gray block">
                              ({product.quantity_in_stock} {product.unit_type || 'units'} total, {product.units_per_package} per pack)
                            </span>
                          </>
                        ) : (
                          <>
                            <span className={`text-sm font-medium ${
                              product.quantity_in_stock <= product.min_stock_level
                                ? 'text-brand-red'
                                : 'text-brand-green'
                            }`}>
                              {product.quantity_in_stock} {product.unit_type || 'units'}
                            </span>
                            {product.allow_partial_sale && product.units_per_package > 0 && (
                              <span className="text-xs text-neutral-gray block">
                                ({product.units_per_package} per pack)
                              </span>
                            )}
                          </>
                        )}
                        {product.quantity_in_stock <= product.min_stock_level && (
                          <AlertTriangle size={14} className="inline text-brand-red ml-1" />
                        )}
                        {product.expiry_date && new Date(product.expiry_date) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                          <p className="text-xs text-brand-red">
                            Expires {new Date(product.expiry_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-dark">
                      KES {parseFloat(product.selling_price).toLocaleString()}
                      {product.price_per_unit && (
                        <span className="text-xs text-neutral-gray block">
                          KES {parseFloat(product.price_per_unit).toFixed(2)}/{product.unit_type}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => { setShowStockModal(product); setStockType('add'); }}
                          className="px-2 py-1 text-xs font-medium text-brand-green hover:bg-brand-green-50 rounded-lg"
                          title="Add stock"
                        >
                          + Stock
                        </button>
                        <button
                          onClick={() => { setShowStockModal(product); setStockType('remove'); }}
                          className="px-2 py-1 text-xs font-medium text-brand-red hover:bg-brand-red-50 rounded-lg"
                          title="Remove stock"
                        >
                          - Stock
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ id: product.id, name: product.name, type: 'product' })}
                          className="p-1.5 text-xs font-medium text-red-400 hover:text-brand-red hover:bg-red-50 rounded-lg"
                          title="Delete product"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-neutral-medium overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-light">
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Service</th>
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Type</th>
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Duration</th>
                  <th className="text-left text-sm font-medium text-neutral-gray px-4 py-3">Status</th>
                  <th className="text-right text-sm font-medium text-neutral-gray px-4 py-3">Price</th>
                  <th className="text-right text-sm font-medium text-neutral-gray px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredServices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-neutral-gray">No services found</td>
                  </tr>
                ) : filteredServices.map(service => (
                  <tr key={service.id} className="border-t border-neutral-medium hover:bg-neutral-light transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-neutral-dark">{service.name}</p>
                      {service.description && (
                        <p className="text-xs text-neutral-gray mt-0.5">{service.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="capitalize text-sm text-neutral-gray">{service.service_type?.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-gray">
                      {service.duration_minutes ? `${service.duration_minutes} min` : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        service.is_active
                          ? 'badge-success'
                          : 'badge-error'
                      }`}>
                        {service.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-neutral-dark">
                      KES {parseFloat(service.price).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => toggleServiceStatus(service)}
                          className={`px-2 py-1 text-xs font-medium rounded-lg ${
                            service.is_active
                              ? 'text-brand-red hover:bg-brand-red-50'
                              : 'text-brand-green hover:bg-brand-green-50'
                          }`}
                        >
                          {service.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ id: service.id, name: service.name, type: 'service' })}
                          className="p-1.5 text-xs font-medium text-red-400 hover:text-brand-red hover:bg-red-50 rounded-lg"
                          title="Delete service"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-neutral-medium px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-semibold text-neutral-dark">Add Product</h2>
              <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-neutral-light rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddProduct} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Product Name <span className="text-brand-red">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} required
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">SKU <span className="text-brand-red">*</span></label>
                  <div className="flex space-x-2">
                    <input type="text" name="sku" value={formData.sku} onChange={handleInputChange} required
                      className="flex-1 px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                    <button
                      type="button"
                      onClick={() => {
                        const name = formData.name.trim();
                        if (!name) {
                          toast.error('Enter product name first to generate SKU');
                          return;
                        }
                        const prefix = formData.category.substring(0, 3).toUpperCase();
                        const namePart = name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 5).toUpperCase();
                        const timestamp = Date.now().toString().slice(-4);
                        const generatedSku = `${prefix}-${namePart}-${timestamp}`;
                        setFormData(prev => ({ ...prev, sku: generatedSku }));
                        toast.success('SKU generated');
                      }}
                      className="px-3 py-2 bg-brand-green text-white rounded-xl text-sm font-medium hover:bg-brand-green-700 transition-colors whitespace-nowrap"
                    >
                      Generate
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Category</label>
                  <select name="category" value={formData.category} onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white">
                    <option value="supplement">Supplement</option>
                    <option value="wellness">Wellness</option>
                    <option value="package">Package</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Selling Price (KES) <span className="text-brand-red">*</span></label>
                  <input type="number" name="selling_price" value={formData.selling_price} onChange={handleInputChange} required min="0" step="0.01"
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Purchase Price (KES)</label>
                  <input type="number" name="purchase_price" value={formData.purchase_price} onChange={handleInputChange} min="0" step="0.01"
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>

                {/* Initial Stock - shown only when partial sale is NOT checked */}
                {!formData.allow_partial_sale && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Initial Stock</label>
                    <input type="number" name="quantity_in_stock" value={formData.quantity_in_stock} onChange={handleInputChange} min="0"
                      className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Min Stock Level</label>
                  <input type="number" name="min_stock_level" value={formData.min_stock_level} onChange={handleInputChange} min="0"
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Unit Type</label>
                  <select name="unit_type" value={formData.unit_type} onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white">
                    <option value="tablets">Tablets</option>
                    <option value="capsules">Capsules</option>
                    <option value="bottles">Bottles</option>
                    <option value="boxes">Boxes</option>
                    <option value="pieces">Pieces</option>
                    <option value="packs">Packs</option>
                    <option value="units">Units</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Expiry Date</label>
                  <input type="date" name="expiry_date" value={formData.expiry_date} onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
              </div>

              {/* Partial Sale Section */}
              <div className="bg-neutral-light rounded-xl p-4 space-y-3">
                <div className="flex items-center space-x-2">
                  <input type="checkbox" name="allow_partial_sale" checked={formData.allow_partial_sale} onChange={handleInputChange}
                    className="rounded border-neutral-medium text-brand-green focus:ring-brand-green" />
                  <label className="text-sm font-medium text-gray-700">Allow partial sales (sell individual units)</label>
                </div>

                {formData.allow_partial_sale && (
                  <div className="grid grid-cols-2 gap-4 pl-6 border-l-2 border-brand-green ml-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Number of Bottles/Packs <span className="text-brand-red">*</span>
                      </label>
                      <input type="number" name="packages_quantity" value={formData.packages_quantity} onChange={handleInputChange} min="0"
                        placeholder="e.g., 10"
                        className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                      <p className="text-xs text-neutral-gray mt-1">How many bottles/packs do you have?</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Units per Bottle/Pack <span className="text-brand-red">*</span>
                      </label>
                      <input type="number" name="units_per_package" value={formData.units_per_package} onChange={handleInputChange} min="0"
                        placeholder="e.g., 30"
                        className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                      <p className="text-xs text-neutral-gray mt-1">How many {formData.unit_type} per bottle/pack?</p>
                    </div>
                    {formData.packages_quantity && formData.units_per_package && (
                      <div className="col-span-2 bg-brand-green-50 rounded-lg p-3 text-sm">
                        <p className="text-brand-green-700 font-medium">Stock Summary</p>
                        <p className="text-brand-green-600">
                          {parseInt(formData.packages_quantity) || 0} bottles/packs × {parseInt(formData.units_per_package) || 0} {formData.unit_type} per pack 
                          = <strong>{(parseInt(formData.packages_quantity) || 0) * (parseInt(formData.units_per_package) || 0)} total {formData.unit_type}</strong>
                        </p>
                        <p className="text-brand-green-600">
                          Price per {formData.unit_type}: KES {(parseFloat(formData.selling_price) / (parseInt(formData.units_per_package) || 1)).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-medium">
                <button type="button" onClick={() => setShowAddModal(false)}
                  className="px-5 py-2.5 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
                  {submitting ? <span className="flex items-center space-x-2"><Loader2 size={16} className="animate-spin" /><span>Adding...</span></span> : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Service Modal */}
      {showAddService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-neutral-medium flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-dark">Add Service</h2>
              <button onClick={() => setShowAddService(false)} className="p-1 hover:bg-neutral-light rounded-lg"><X size={20} /></button>
            </div>
            <form onSubmit={handleAddService} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Service Name <span className="text-brand-red">*</span></label>
                <input type="text" name="name" value={serviceForm.name} onChange={(e) => setServiceForm(p => ({...p, name: e.target.value}))} required
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Price (KES) <span className="text-brand-red">*</span></label>
                  <input type="number" name="price" value={serviceForm.price} onChange={(e) => setServiceForm(p => ({...p, price: e.target.value}))} required min="0" step="0.01"
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration (minutes)</label>
                  <input type="number" name="duration_minutes" value={serviceForm.duration_minutes} onChange={(e) => setServiceForm(p => ({...p, duration_minutes: e.target.value}))} min="0"
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Service Type</label>
                <select name="service_type" value={serviceForm.service_type} onChange={(e) => setServiceForm(p => ({...p, service_type: e.target.value}))}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white">
                  <option value="consultation">Consultation</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="program">Program</option>
                  <option value="assessment">Assessment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                <textarea name="description" value={serviceForm.description} onChange={(e) => setServiceForm(p => ({...p, description: e.target.value}))} rows={3}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-medium">
                <button type="button" onClick={() => setShowAddService(false)}
                  className="px-5 py-2.5 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
                  {submitting ? <span className="flex items-center space-x-2"><Loader2 size={16} className="animate-spin" /><span>Adding...</span></span> : 'Add Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="px-6 py-4 border-b border-neutral-medium flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-dark">Confirm Delete</h2>
              <button onClick={() => setDeleteConfirm(null)} className="p-1 hover:bg-neutral-light rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-neutral-gray">
                Are you sure you want to delete <span className="font-medium text-neutral-dark">{deleteConfirm.name}</span>?
              </p>
              <p className="text-xs text-neutral-gray">
                This {deleteConfirm.type} will be deactivated and hidden from lists. Historical records will be preserved.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2.5 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 bg-brand-red text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                >
                  {submitting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Update Modal */}
      {showStockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="px-6 py-4 border-b border-neutral-medium flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-dark">
                {stockType === 'add' ? 'Add Stock' : 'Remove Stock'}
              </h2>
              <button onClick={() => { setShowStockModal(null); setStockQuantity(1); setStockMode('individual'); }} className="p-1 hover:bg-neutral-light rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-neutral-gray">
                Product: <span className="font-medium text-neutral-dark">{showStockModal.name}</span>
              </p>
              <div className="bg-neutral-light rounded-lg p-3 space-y-1">
                <p className="text-sm text-neutral-gray">
                  Current Stock: <span className="font-medium text-neutral-dark">{showStockModal.quantity_in_stock} {showStockModal.unit_type || 'units'}</span>
                </p>
                {showStockModal.allow_partial_sale && showStockModal.packages_quantity > 0 && (
                  <p className="text-sm text-neutral-gray">
                    Packages: <span className="font-medium text-neutral-dark">{showStockModal.packages_quantity} bottles/packs</span>
                  </p>
                )}
                {showStockModal.allow_partial_sale && showStockModal.units_per_package > 0 && (
                  <p className="text-sm text-neutral-gray">
                    Per Package: <span className="font-medium text-neutral-dark">{showStockModal.units_per_package} {showStockModal.unit_type}</span>
                  </p>
                )}
              </div>
              {/* Stock mode toggle for partial sale products */}
              {showStockModal.allow_partial_sale && showStockModal.units_per_package > 0 && (
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setStockMode('individual')}
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                      stockMode === 'individual'
                        ? 'bg-brand-green text-white border-brand-green'
                        : 'bg-white text-gray-700 border-neutral-medium hover:bg-neutral-light'
                    }`}
                  >
                    By {showStockModal.unit_type || 'Unit'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStockMode('package')}
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                      stockMode === 'package'
                        ? 'bg-brand-green text-white border-brand-green'
                        : 'bg-white text-gray-700 border-neutral-medium hover:bg-neutral-light'
                    }`}
                  >
                    By Package
                  </button>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Quantity to {stockType === 'add' ? 'add' : 'remove'}
                  {stockMode === 'package' && ' (in bottles/packs)'}
                </label>
                <input type="number" value={stockQuantity} onChange={(e) => setStockQuantity(Math.max(1, parseInt(e.target.value) || 1))} min="1"
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green" />
              </div>
              <button onClick={handleStockUpdate} disabled={submitting}
                className={`w-full py-2.5 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50 ${
                  stockType === 'add' ? 'bg-brand-green hover:bg-brand-green-700' : 'bg-brand-red hover:bg-brand-red-700'
                }`}>
                {submitting ? <span className="flex items-center justify-center space-x-2"><Loader2 size={16} className="animate-spin" /><span>Updating...</span></span>
                  : `${stockType === 'add' ? 'Add' : 'Remove'} Stock`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;